/**
 * The headbands, named — ONE mapping, in one module (Sprint 1.9.2 S4).
 *
 * `RecordingRecord.device_id` is the profile the backend RESOLVED from the
 * archive (`brainbit_pro` / `brainbit`), and the contract's enum is exactly
 * these two. The name and the contact count follow from that id alone, which is
 * why they are written here once rather than at each screen: the capture flow
 * (`lib/capture/session.ts`), the recording card, the page headers and the
 * Baseline picker all read this table, so the words for a headband cannot drift
 * apart between the screen that records it and the screens that show it.
 *
 * Device metadata, not science: no threshold, no reading and no payload string
 * lives here, and `clientScience.test.ts` scans this file like every other.
 */
import type { DeviceId, RecordingRecord } from './api'

export type { DeviceId }

/** In display order: the reference device first. */
export const DEVICE_IDS: readonly DeviceId[] = ['brainbit_pro', 'brainbit']

/**
 * The eight-channel BrainBit Pro: the reference device, and the one whose
 * eight sensors the Daily Status montage draws. Named once so the screens that
 * branch on "is this the Pro" spell it the same way.
 */
export const PRO_DEVICE_ID: DeviceId = 'brainbit_pro'

/** "<name> · <contacts> contacts" — the wording the capture flow shipped with. */
export const DEVICE_LABELS: Readonly<Record<DeviceId, string>> = {
  brainbit_pro: 'BrainBit Pro · 8 contacts',
  brainbit: 'BrainBit · 4 contacts',
}

/**
 * The label for a device id, or `null` for one this build does not know.
 *
 * `null`, never a guess: an id outside the table is a device this client has
 * never been told about, and naming it — or giving it a contact count — would
 * be a fact about the recording the client does not have.
 */
export function deviceLabel(id: string | null | undefined): string | null {
  if (id == null) return null
  return isDeviceId(id) ? DEVICE_LABELS[id] : null
}

export function isDeviceId(id: string): id is DeviceId {
  return (DEVICE_IDS as readonly string[]).includes(id)
}

/**
 * The device a recording was made with, as a `device_id` the backend admits —
 * or `undefined` when the value is not one, so the caller asserts NOTHING and
 * the server detects the montage itself. Never a default: an unknown layout is
 * not a Pro.
 */
export function asDeviceId(id: string | null | undefined): DeviceId | undefined {
  return id != null && isDeviceId(id) ? id : undefined
}

/** The headbands in these rows, in `DEVICE_IDS` order. An id this build cannot name is left out. */
export function recordedDevices(rows: readonly RecordingRecord[]): DeviceId[] {
  const seen = new Set<string>(rows.map((row) => row.device_id))
  return DEVICE_IDS.filter((id) => seen.has(id))
}

/**
 * Whose baseline a screen reads — Baseline, and Home's baselines card — in
 * this order:
 *
 *   1. the headband the user picked, while it is one they have recorded with;
 *   2. the headband of their NEWEST upload — page one's first row, since the
 *      list is newest-first — which is the one they are recording with now,
 *      and which does not move as later pages arrive;
 *   3. the BrainBit Pro, the backend's own default when `device_id` is omitted
 *      and the only baseline anyone with no recordings could have.
 *
 * A choice, not a measurement: nothing here reads a baseline or a score.
 */
export function baselineDevice(
  rows: readonly RecordingRecord[],
  recorded: readonly DeviceId[],
  chosen: DeviceId | null,
): DeviceId {
  if (chosen !== null && recorded.includes(chosen)) return chosen
  const newest = rows[0]?.device_id
  if (newest !== undefined && isDeviceId(newest)) return newest
  return PRO_DEVICE_ID
}
